import { createContext, FC, PropsWithChildren, useContext, useEffect, useState } from 'react';
import { logger } from '@finance/shared';
import { HiddenFeatureKey, HiddenFeaturesMap, useAuthLink } from 'lib/comlink/AuthLink';

const HiddenFeaturesContext = createContext<HiddenFeaturesMap>({});

type HiddenFeaturesProviderProps = PropsWithChildren<{
    // Test-only: bypass the Comlink pull and use a fixed map.
    initialMap?: HiddenFeaturesMap;
}>;

// Pull on mount: the iframe remounts when the user re-enters finance,
// so a single pull is always fresh — no subscription needed.
export const HiddenFeaturesProvider: FC<HiddenFeaturesProviderProps> = ({ children, initialMap }) => {
    const authLink = useAuthLink();
    const [map, setMap] = useState<HiddenFeaturesMap>(() => initialMap ?? {});

    useEffect(() => {
        if (initialMap || !authLink) {
            return;
        }

        authLink
            .triggerAppCallback<HiddenFeaturesMap>('getHiddenFeatures')
            .then(pulled => setMap(pulled ?? {}))
            .catch(error => {
                logger.warn('HiddenFeaturesProvider: pull from parent failed; using empty map', error);
            });
    }, [authLink, initialMap]);

    return <HiddenFeaturesContext.Provider value={map}>{children}</HiddenFeaturesContext.Provider>;
};

export const useIsFeatureHidden = (feature: HiddenFeatureKey): boolean => {
    const map = useContext(HiddenFeaturesContext);
    return !!map[feature];
};
